import { subscribeToConversation } from '../channels/conversation_channel'

export default class SellerChatController {
  async initialize() {
    this.currentUserId = parseInt(document.querySelector('meta[name="current-user-id"]')?.content, 10)
    this.conversationId = null
    this.subscription = null
    this.sentMessageIds = new Set()

    if (this.currentUrlValue) {
      try {
        const response = await fetch(this.currentUrlValue, {
          headers: { 'Accept': 'application/json' }
        })
        if (response.ok) {
          const data = await response.json()
          if (data.conversation_id) {
            this.switchToChatMode(data.conversation_id, data.messages)
          }
        }
      } catch (e) {
        // Silently fail — user stays in contact form mode
      }
    }
  }

  switchToChatMode(conversationId, messages = []) {
    this.conversationId = conversationId

    // Replace body content with messages container + input
    const body = this.element.querySelector('.seller-chat__body')
    if (body) {
      body.innerHTML = '<div class="seller-chat__messages" data-seller-chat-target="messagesArea"></div>'
      const textarea = document.createElement('textarea')
      textarea.className = 'seller-chat__textarea'
      textarea.setAttribute('data-seller-chat-target', 'input')
      textarea.rows = 1
      textarea.placeholder = 'Escribí un mensaje...'
      body.appendChild(textarea)

      const feedback = document.createElement('div')
      feedback.className = 'seller-chat__feedback'
      feedback.setAttribute('data-seller-chat-target', 'feedback')
      feedback.style.display = 'none'
      body.appendChild(feedback)

      this.inputTarget = textarea
      this.feedbackTarget = feedback
      this.messagesArea = body.querySelector('.seller-chat__messages')
    }

    // Update footer button
    const footer = this.element.querySelector('.seller-chat__footer')
    if (footer) {
      this.sendBtnTarget.textContent = 'Enviar'
    }

    // Render existing messages
    if (messages.length > 0) {
      messages.forEach(msg => this.appendMessage(msg))
      this.scrollToBottom()
    }

    // Subscribe to channel
    this.subscription = subscribeToConversation(conversationId, (msg) => this.handleIncomingMessage(msg))

    // Re-bind events for new input
    this.inputTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.send()
      }
    })
  }

  async send() {
    const body = this.inputTarget.value.trim()
    if (!body) return

    this.sendBtnTarget.disabled = true
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Determine endpoint
    const url = this.conversationId
      ? `/conversations/${this.conversationId}/messages`
      : this.urlValue

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ body })
    })
    .then(response => {
      if (!response.ok) return response.json().then(data => { throw new Error(data.error || 'Error al enviar') })
      return response.json()
    })
    .then(data => {
      this.inputTarget.value = ''

      if (!this.conversationId) {
        // First message — switch to chat mode
        const conversationId = data.conversation_id
        const message = data.message
        this.switchToChatMode(conversationId)
        this.sentMessageIds.add(message.id)
        this.appendMessage({
          id: message.id,
          body: message.body,
          sender_id: this.currentUserId,
          created_at: message.created_at
        })
        this.scrollToBottom()
      } else {
        // Subsequent message in chat mode
        const messageId = data.id || data.message?.id
        const messageBody = data.body || data.message?.body
        const createdAt = data.created_at || data.message?.created_at
        this.sentMessageIds.add(messageId)
        this.appendMessage({
          id: messageId,
          body: messageBody,
          sender_id: this.currentUserId,
          created_at: createdAt
        })
        this.scrollToBottom()
      }
    })
    .catch(error => {
      this.feedbackTarget.style.display = 'block'
      this.feedbackTarget.className = 'seller-chat__feedback seller-chat__feedback--error'
      this.feedbackTarget.textContent = error.message
    })
    .finally(() => {
      this.sendBtnTarget.disabled = false
    })
  }

  handleIncomingMessage(msg) {
    // Skip own messages (already optimistically appended)
    if (this.sentMessageIds.has(msg.id)) return

    this.appendMessage(msg)
    this.scrollToBottom()
  }

  appendMessage(msg) {
    if (!this.messagesArea) return

    const isMine = msg.sender_id === this.currentUserId
    const time = new Date(msg.created_at).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
    const html = `
      <div class="message ${isMine ? 'message--mine' : 'message--theirs'}">
        <div class="message__bubble">
          <p class="message__body">${this.escapeHtml(msg.body)}</p>
          <time class="message__time">${time}</time>
        </div>
      </div>
    `
    this.messagesArea.insertAdjacentHTML('beforeend', html)
  }

  scrollToBottom() {
    if (this.messagesArea) {
      this.messagesArea.scrollTop = this.messagesArea.scrollHeight
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-controller="seller-chat"]').forEach(el => {
    const controller = new SellerChatController()
    controller.element = el
    controller.urlValue = el.dataset.sellerChatUrlValue
    controller.currentUrlValue = el.dataset.sellerChatCurrentUrlValue
    controller.inputTarget = el.querySelector('[data-seller-chat-target="input"]')
    controller.sendBtnTarget = el.querySelector('[data-seller-chat-target="sendBtn"]')
    controller.feedbackTarget = el.querySelector('[data-seller-chat-target="feedback"]')

    if (!controller.inputTarget || !controller.sendBtnTarget) return

    controller.sendBtnTarget.addEventListener('click', (e) => {
      e.preventDefault()
      controller.send()
    })

    controller.inputTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        controller.send()
      }
    })

    controller.initialize()
  })
})
